import React, { useRef, useState, useEffect, useMemo, forwardRef, useCallback } from 'react';
import Globe from 'react-globe.gl';
import * as THREE from 'three';
import './Globe.css';
import { createStarBackground } from './createStarBackground';
import GlobeLoader from './GlobeLoader';
import { useTheme } from '@emotion/react';
import { Box, Typography, Fade, Snackbar } from '@mui/material';

const GlobeComponent = React.forwardRef(function GlobeComponent({ pointsData = [], highlightedCoin = null, trackData = null, updateCameraPosition = null, backgroundControls = null, dataLoading = false, config = null }, ref) {
  const globeRef = ref || useRef();
  const reactGlobeRef = useRef();
  const controlsRef = useRef();
  const [isRotatingToTarget, setIsRotatingToTarget] = useState(false)
  const [targetRotation, setTargetRotation] = useState({ x: 0, y: 0 })
  const [hasInteracted, setHasInteracted] = useState(false)
  const hasInteractedRef = useRef(false)
  hasInteractedRef.current = hasInteracted
  const starsBackgroundRef = useRef(null)
  const pointsDataRef = useRef(pointsData);
  const [internalHighlightedCoin, setInternalHighlightedCoin] = useState(null)
  const [internalTrackData, setInternalTrackData] = useState(null)
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const [visibleCoinIds, setVisibleCoinIds] = useState(null)
  const [clickedCoinId, setClickedCoinId] = useState(null)
  const [isHoveringCoin, setIsHoveringCoin] = useState(false)
  const [isHoveringArc, setIsHoveringArc] = useState(false)
  const [storedTrackData, setStoredTrackData] = useState(null)
  const loading = dataLoading

  const [animateGlobe, setAnimateGlobe] = useState(false)
  const lastClickTime = useRef(0)
  const clickTimeout = useRef(null)
  const [infoMessage, setInfoMessage] = useState('')
  const [showMessage, setShowMessage] = useState(false)

  useEffect(() => {
    const scene = reactGlobeRef.current?.scene();
    const camera = reactGlobeRef.current?.camera();
    if (!scene || !camera) return;

    const handleDiscClick = (message) => {
      setInfoMessage(message);
      setShowMessage(true);
    };

    const stars = createStarBackground(scene, camera, handleDiscClick, config);
    starsBackgroundRef.current = stars;
    return () => scene.remove(stars);
  }, []);

  useEffect(() => {
    if (!loading && reactGlobeRef.current) {
      const controls = reactGlobeRef.current.controls()
      const camera = reactGlobeRef.current.camera()

      if (!controls || !camera) return

      const initialPosition = camera.position.clone()
      const target = new THREE.Vector3(0, 0, 0)
      const startDistance = initialPosition.distanceTo(target)
      const targetDistance = 400
      const duration = 2000
      const startTime = Date.now()

      const direction = initialPosition.clone().normalize()
      const animateCamera = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)

        const easeProgress = 1 - Math.pow(1 - progress, 3)

        const newDistance = startDistance + (targetDistance - startDistance) * easeProgress

        const newPosition = direction.clone().multiplyScalar(newDistance)
        camera.position.copy(newPosition)

        camera.lookAt(target)
        controls.update()

        if (progress < 1) {
          requestAnimationFrame(animateCamera)
        } else {
          setAnimateGlobe(true)
        }
      }

      requestAnimationFrame(animateCamera)
    }
  }, [loading, reactGlobeRef, reactGlobeRef.current])

  useEffect(() => {
    pointsDataRef.current = pointsData
  }, [pointsData])

  const [controlsDistance, setControlsDistance] = useState(0)
  const lastControlsDistance = useRef(0)
  useEffect(() => {
    if (reactGlobeRef.current) {
      const controls = reactGlobeRef.current.controls()
      const camera = reactGlobeRef.current.camera()
      controls.minDistance = 101
      controls.maxDistance = 500
      controls.enablePan = false
      controls.enableDamping = true
      controls.dampingFactor = 0.1
      controls.rotateSpeed = 0.8

      // Enable multitouch gestures for pinch zoom on mobile
      controls.touches = {
        ONE: THREE.TOUCH.ROTATE,
        TWO: THREE.TOUCH.DOLLY_PAN // Enable two-finger gestures (pinch)
      }

      const onControlsChange = () => {
        updateCameraPosition(camera.position)
        setControlsDistance(controls.getDistance())
        const max = 300
        const min = 0
        const desiredLightIntensity = (controls.getDistance() - min) / (max - min)
        const scene = reactGlobeRef.current.scene()
        scene.traverse((child) => {
          if (child.isLight) {
            child.intensity += (desiredLightIntensity - child.intensity) * 0.1
          }
        })
        if (lastControlsDistance.current !== 0 && controls.getDistance() - lastControlsDistance.current > 1.1 && controls.getDistance() > 300) {
          if (hasInteractedRef.current) {
            setHasInteracted(false)
            starsBackgroundRef.current.fadeInDisc()
          }
        } else {
        }
        lastControlsDistance.current = controls.getDistance()
      }

      const handleFirstInteraction = () => {
        if (!hasInteracted && starsBackgroundRef.current) {
          setHasInteracted(true)
          starsBackgroundRef.current.fadeOutDisc()
        }
      }

      controls.addEventListener('change', onControlsChange)
      controls.addEventListener('start', handleFirstInteraction)

      // Add additional events to ensure we catch all user interactions
      window.addEventListener('mousedown', handleFirstInteraction)
      window.addEventListener('wheel', handleFirstInteraction, { passive: true })
      window.addEventListener('touchstart', handleFirstInteraction, { passive: true })

      onControlsChange()

      return () => {
        controls.removeEventListener('change', onControlsChange)
        controls.removeEventListener('start', handleFirstInteraction)
        window.removeEventListener('mousedown', handleFirstInteraction)
        window.removeEventListener('wheel', handleFirstInteraction)
        window.removeEventListener('touchstart', handleFirstInteraction)
      }
    }
  }, [reactGlobeRef, reactGlobeRef.current, backgroundControls, backgroundControls.current, reactGlobeRef.current?.controls()])

  useEffect(() => {
    const handleResize = () => {
      if (globeRef.current && globeRef.current.__globeObj) {
        const globe = globeRef.current.__globeObj
        const camera = globe.children.find(child => child instanceof THREE.PerspectiveCamera)
        const renderer = globeRef.current.__renderer

        if (camera && renderer) {
          const width = window.innerWidth
          const height = window.innerHeight

          camera.aspect = width / height
          camera.updateProjectionMatrix()

          renderer.setSize(width, height)
          renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        }
      }
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    pointsDataRef.current = pointsData
  }, [pointsData])

  const clearCoinTrack = useCallback(() => {
    setInternalTrackData(null)
  }, [])

  const showCoinTrack = useCallback((trackPoints) => {
    clearCoinTrack()
    if (!trackPoints) return

    setInternalTrackData(trackPoints)
  }, [clearCoinTrack])

  const clearHighlight = useCallback(() => {
    setInternalHighlightedCoin(null)
    clearCoinTrack()
    setVisibleCoinIds(null)
    setClickedCoinId(null)
  }, [clearCoinTrack])

  useEffect(() => {
  }, [internalTrackData])

  const highlightCoin = useCallback((coinData) => {
    clearHighlight()

    if (!coinData || !coinData.latitude || !coinData.longitude) return

    setInternalHighlightedCoin(coinData)

    const locations = pointsDataRef.current.filter(point => point.coinid === coinData.coinid)

    if (locations.length >= 2) {
      const sortedLocations = [...locations].sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        return dateA - dateB
      })

      const trackArcs = []
      sortedLocations.sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        return dateA - dateB
      })

      for (let i = 0; i < sortedLocations.length - 1; i++) {
        trackArcs.push({
          startLat: sortedLocations[i].latitude,
          startLng: sortedLocations[i].longitude,
          endLat: sortedLocations[i + 1].latitude,
          endLng: sortedLocations[i + 1].longitude,
        })
      }

      showCoinTrack(trackArcs)
      setCoinsVisible([coinData.coinid])
      isCoinHighlighted.current = true
    } else if (coinData.coinid === 1) {
      setCoinsVisible([coinData.coinid])
      return;
    }

    if (globeRef.current) {
      const position = latLngToVector3(coinData.latitude, coinData.longitude, 1)
      rotateGlobeToShowPoint(position)
    }
  }, [clearHighlight, pointsData, showCoinTrack])

  const setCoinsVisible = (coins) => {
    setVisibleCoinIds(coins ? new Set(coins) : null)
  }

  const latLngToVector3 = useCallback((lat, lng, radius = 1) => {
    const phi = (90 - lat) * (Math.PI / 180)
    const theta = (lng + 180) * (Math.PI / 180)

    const x = -radius * Math.sin(phi) * Math.cos(theta)
    const y = radius * Math.cos(phi)
    const z = radius * Math.sin(phi) * Math.sin(theta)

    return new THREE.Vector3(x, y, z)
  }, [])

  const coinGeometry = useMemo(() => new THREE.CylinderGeometry(0.2, 0.2, 0.05, 32), [])
  const coinMaterial = useMemo(() => {
    const textureLoader = new THREE.TextureLoader()
    const coinTexture = textureLoader.load('/tkn.png')
    coinTexture.minFilter = THREE.LinearMipMapLinearFilter
    coinTexture.magFilter = THREE.LinearMipMapLinearFilter
    coinTexture.colorSpace = THREE.SRGBColorSpace
    return new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: coinTexture,
      side: THREE.DoubleSide,
      transparent: true,
      roughness: 0.2,
      metalness: 0.1,
      depthWrite: false,
    })
  }, [])

  const createCoinObject = useCallback((d) => {
    const mesh = new THREE.Mesh(coinGeometry, coinMaterial)
    const t = (controlsDistance - 100) / 200
    const scale = Math.max(0, Math.min(1, t)) * 1.9 + 0.1
    mesh.scale.set(scale, scale, scale)
    const coinIndex = pointsDataRef.current.findIndex(point => point.coinid === d.coinid)
    mesh.rotation.y = Math.PI * 2 * coinIndex / pointsDataRef.current.length
    mesh.rotation.x = Math.PI / 2
    mesh.userData = d
    mesh.layers.enable(2)
    return mesh
  }, [coinGeometry, coinMaterial, controlsDistance])

  const isCoinHighlighted = useRef(false)

  const handleObjectHover = useCallback((obj) => {

    let coinData = null

    try {
      if (obj && obj.__data) {
        coinData = obj.__data
      } else {
        coinData = obj
      }
    } catch (error) {
      console.log('Err ', error);
      setIsHoveringCoin(false)
      setInternalTrackData(null)
      return
    }

    if (!obj && !isCoinHighlighted.current) {
      setIsHoveringCoin(false)
      setInternalTrackData(null)

      if (!isHoveringArc && !clickedCoinId && storedTrackData) {
        setInternalTrackData(null)
      }
      return
    }

    setIsHoveringCoin(true)

    if (!coinData || !coinData.coinid || !coinData.latitude || !coinData.longitude) {

      setIsHoveringCoin(false)
      return
    }

    if (clickedCoinId && clickedCoinId !== coinData.coinid) {
      setIsHoveringCoin(false)
      return
    }
    const locations = pointsDataRef.current.filter(point => point.coinid === coinData.coinid)

    if (locations.length >= 2) {
      const sortedLocations = [...locations].sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        return dateA - dateB
      })

      const trackArcs = []
      for (let i = 0; i < sortedLocations.length - 1; i++) {
        trackArcs.push({
          startLat: sortedLocations[i].latitude,
          startLng: sortedLocations[i].longitude,
          endLat: sortedLocations[i + 1].latitude,
          endLng: sortedLocations[i + 1].longitude,
        })
      }

      // Update the track data
      setInternalTrackData(trackArcs)
    }
  }, [pointsDataRef, clickedCoinId])

  const handleObjectClick = useCallback((obj) => {
    if (!obj) return;

    let coinData;

    try {
      if (obj.__globeObjType === null) return;
      coinData = obj.__data || obj;
    } catch (error) {
      console.log('Err ', error);
      return;
    }

    if (!coinData || !coinData.coinid) return;

    setClickedCoinId(coinData.coinid);

    // Then proceed with normal highlight
    if (obj.__data) {
      highlightCoin(obj.__data)
    } else {
      highlightCoin(obj)
    }
  }, [highlightCoin])

  const rotateGlobeToShowPoint = useCallback((position) => {
    if (!globeRef.current) return

    const target = position.clone().normalize()
    const targetRotationY = Math.atan2(target.x, target.z)
    const targetRotationX = Math.atan2(target.y, Math.sqrt(target.x * target.x + target.z * target.z))

    setTargetRotation({ x: targetRotationX, y: targetRotationY })
    setIsRotatingToTarget(true)
  }, [])

  useEffect(() => {
    if (!globeRef.current || !isRotatingToTarget) return

    const rotationAnimationRef = { current: null }

    const animate = () => {
      if (!globeRef.current || !isRotatingToTarget) return

      const globe = globeRef.current.__globeObj
      if (!globe) return

      globe.rotation.y = THREE.MathUtils.lerp(globe.rotation.y, targetRotation.y, 0.05)
      globe.rotation.x = THREE.MathUtils.lerp(globe.rotation.x, targetRotation.x, 0.05)

      if (Math.abs(globe.rotation.y - targetRotation.y) < 0.01 &&
        Math.abs(globe.rotation.x - targetRotation.x) < 0.01) {
        setIsRotatingToTarget(false)
        return
      }

      rotationAnimationRef.current = requestAnimationFrame(animate)
    }

    rotationAnimationRef.current = requestAnimationFrame(animate)

    return () => {
      if (rotationAnimationRef.current) {
        cancelAnimationFrame(rotationAnimationRef.current)
      }
    }
  }, [isRotatingToTarget, targetRotation])

  useEffect(() => {
    if (!globeRef.current || isRotatingToTarget) return

    const animateFrame = () => {
      if (!globeRef.current || isRotatingToTarget) return

      const globe = globeRef.current.__globeObj
      if (!globe) return

      globe.rotation.y += 0.0005

      requestAnimationFrame(animateFrame)
    }

    const animationId = requestAnimationFrame(animateFrame)

    return () => cancelAnimationFrame(animationId)
  }, [isRotatingToTarget])

  const handleGlobeDoubleClick = useCallback((globe, event) => {
    if (!reactGlobeRef.current) return

    const controls = reactGlobeRef.current.controls()
    const camera = reactGlobeRef.current.camera()

    if (!controls || !camera) return

    const targetDistance = isZoomedIn ? 400 : 150
    const duration = 1000
    const startTime = Date.now()
    const startPosition = camera.position.clone()
    const startDistance = startPosition.length()
    const direction = startPosition.clone().normalize()

    const animateZoom = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easeProgress = 1 - Math.pow(1 - progress, 3)

      const newDistance = startDistance + (targetDistance - startDistance) * easeProgress
      const newPosition = direction.clone().multiplyScalar(newDistance)

      camera.position.copy(newPosition)
      camera.lookAt(0, 0, 0)
      controls.update()

      if (progress < 1) {
        requestAnimationFrame(animateZoom)
      } else {
        setIsZoomedIn(!isZoomedIn)
      }
    }

    requestAnimationFrame(animateZoom)
  }, [reactGlobeRef, isZoomedIn])

  const handleGlobeClick = useCallback((globe, event) => {
    try {
      if (event && event.preventDefault) {
        event.preventDefault()
      }

      const currentTime = Date.now()
      const timeSinceLastClick = currentTime - lastClickTime.current

      if (timeSinceLastClick < 300) {
        if (clickTimeout.current) {
          clearTimeout(clickTimeout.current)
          clickTimeout.current = null
        }

        handleGlobeDoubleClick(globe, event)
        lastClickTime.current = 0
        return
      }

      lastClickTime.current = currentTime

      clickTimeout.current = setTimeout(() => {
        isCoinHighlighted.current = false
        clearHighlight()
        setVisibleCoinIds(null)
        setInternalTrackData(null)
        setClickedCoinId(null)
        setStoredTrackData(null)
        clickTimeout.current = null
      }, 300)
    } catch (error) {
      // Silently handle any errors during globe click
    }
  }, [clearHighlight, handleGlobeDoubleClick])

  const animateCameraToPosition = useCallback((latitude, longitude) => {
    if (!reactGlobeRef.current) return

    const camera = reactGlobeRef.current.camera()
    const controls = reactGlobeRef.current.controls()

    if (!camera || !controls) return

    const targetPosition = reactGlobeRef.current.getCoords(latitude, longitude, 0.5)
    const startPosition = camera.position.clone()
    const distance = startPosition.distanceTo(targetPosition)
    const baseDuration = 1000
    const maxDuration = 2500
    const duration = Math.min(baseDuration + distance * 10, maxDuration)
    const startTime = Date.now()

    const animate = () => {
      const now = Date.now()
      const progress = Math.min((now - startTime) / duration, 1)
      const easeProgress = 1 - Math.pow(1 - progress, 3)

      if (progress < 1) {
        camera.position.lerpVectors(startPosition, targetPosition, easeProgress)
        camera.lookAt(0, 0, 0)
        controls.update()
        requestAnimationFrame(animate)
      }
    }
    requestAnimationFrame(animate)
  }, [reactGlobeRef])

  React.useImperativeHandle(ref, () => ({
    highlightCoin,
    showCoinTrack,
    clearHighlight,
    animateCameraToPosition,
    camera: () => reactGlobeRef.current?.camera()
  }), [highlightCoin, showCoinTrack, clearHighlight])

  // Resize
  useEffect(() => {
    const handleResize = () => {
      if (reactGlobeRef.current) {
        const camera = reactGlobeRef.current.camera()
        const renderer = reactGlobeRef.current.renderer()

        if (camera && renderer) {
          camera.aspect = window.innerWidth / window.innerHeight
          camera.updateProjectionMatrix()

          renderer.setSize(window.innerWidth, window.innerHeight)
          renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        }
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])


  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  return (
    <>
      <GlobeLoader progress={loadingProgress} visible={dataLoading} dataLoading={dataLoading} loading={dataLoading} textureLoading={false} />
      <Snackbar
        open={showMessage}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            p: 2,
            borderRadius: 1,
            boxShadow: 3
          }}
        >
          <Typography variant="body1">{infoMessage}</Typography>
        </Box>
      </Snackbar>
      <Globe

        ref={reactGlobeRef}
        controls={controlsRef}
        animateIn
        width={window.innerWidth}
        height={window.innerHeight}
        backgroundColor="#000"
        rendererConfig={{ alpha: true, antialias: false, powerPreference: 'high-performance' }}
        backgroundImageUrl={null}
        showAtmosphere={false}
        showGraticules={false}

        // your coin points:
        objectsData={visibleCoinIds ? pointsData.filter(point => visibleCoinIds.has(point.coinid)) : pointsData}
        objectThreeObject={d => {
          const obj = createCoinObject(d)
          obj.__data = d
          obj.__globeObjType = 'object'
          return obj
        }}

        objectLat={d => d.latitude}
        objectLng={d => d.longitude}
        objectAltitude={(d) => {
          return 0
        }}
        objectRotationSpeed={0}
        objectLabel={d => {
          const formattedDate = new Date(d.date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
          return `
                    <div style="
                        font-family: 'Roboto', Arial, sans-serif;
                        backdrop-filter: blur(4px);
                        border-radius: 8px;
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        background: rgba(0, 0, 0, 0.3);
                        padding: 12px;
                        min-width: 160px;
                    ">
                        <div style="
                            font-size: 14px;
                            font-weight: 600;
                            color: #fff;
                            margin-bottom: 8px;
                            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                            padding-bottom: 6px;
                        ">${d.coinid}</div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                            <span style="font-size: 12px; color: rgba(255, 255, 255, 0.6);">Location: </span>
                            <span style="font-size: 12px; color: #fff; font-weight: 500; margin: 0px 5px;">${d.calculatedLocation}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between;">
                            <span style="font-size: 12px; color: rgba(255, 255, 255, 0.6);">Date:</span>
                            <span style="font-size: 12px; color: #fff; font-weight: 500;">${formattedDate}</span>
                        </div>
                    </div>
                `
        }}
        pointerEventsFilter={(obj, data) => {
          if (obj.__globeObjType === 'globe') return true
          if (!obj || typeof obj !== 'object') return false

          // Skip arcs/lines
          const isLine = data && data.endLat && data.endLng
          if (isLine) return false

          // Only allow clicks on objects with data
          return data && data.coinid
        }}
        objectLabelResolution={1}
        objectLabelDotRadius={0}
        objectLabelSize={0.8}
        objectLabelColor={() => '#ffffff'}
        onObjectClick={handleObjectClick}
        onObjectHover={handleObjectHover}
        arcsData={internalTrackData || []}
        arcDashLength={0.4}
        arcDashGap={0.0}
        arcColor={() => t => `rgba(255,100,50,${1 - t * 0.5})`}
        arcDashAnimateTime={1500}
        arcStroke={controlsDistance / 5000 + 0.02}

        arcsTransitionDuration={0}
        arcAltitude={(d) => {
          function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
            var R = 6371; // Radius of the earth in km
            var dLat = deg2rad(lat2 - lat1);  // deg2rad below
            var dLon = deg2rad(lon2 - lon1);
            var a =
              Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2)
              ;
            var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            var d = R * c; // Distance in km
            return d;
          }

          function deg2rad(deg) {
            return deg * (Math.PI / 180)
          }
          // The bigger the distance, the higher the altitude
          const startLat = d.startLat
          const startLng = d.startLng
          const endLat = d.endLat
          const endLng = d.endLng
          const distance = getDistanceFromLatLonInKm(startLat, startLng, endLat, endLng)
          const minDistance = 0
          const maxDistance = 500
          const normalizedDistance = (distance - minDistance) / (maxDistance - minDistance)

          return normalizedDistance * 0.02
        }}
        onGlobeClick={handleGlobeClick}
        globeTileEngineUrl={(x, y, z) => `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`}
      />
    </>
  );
});

export default GlobeComponent;